// ============================================================
//  src/pages/bots/tabs/CommerceTab.js
//
//  In-chat commerce for a bot: connect the OWNER'S OWN PocketFi
//  merchant account, manage a product catalog, and (once both
//  exist) a "request_payment" action in Commands/Flows can sell
//  in the chat itself. Money flows straight to the owner's own
//  PocketFi account — CmdKit never touches it.
//
//  Mirrors InboxTab.js's structure and CSS conventions exactly
//  (modal-overlay/modal-box, field-label/field-input, btn/
//  icon-btn, window.confirm for deletes) so it feels native
//  next to the Inbox tab, not like a bolted-on feature.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import './commerce.css';
import toast from 'react-hot-toast';
import {
  getPocketfiMerchant, savePocketfiMerchant, disablePocketfiMerchant,
  getProducts, createProduct, updateProduct, deleteProduct,
} from '../../../api/client';

function fmtNaira(n) {
  return '₦' + (Number(n) || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 });
}

// ── Main CommerceTab ────────────────────────────────────────
export default function CommerceTab({ bId }) {
  const [activeTab, setActiveTab] = useState('products'); // products | settings
  const [configured, setConfigured] = useState(null); // null = unknown yet

  // Products can't sell without a connected PocketFi account —
  // check once up front so we can nudge the owner toward Settings.
  const checkConfigured = useCallback(async () => {
    try {
      const res = await getPocketfiMerchant(bId);
      setConfigured(!!res.data?.configured);
    } catch { setConfigured(false); }
  }, [bId]);

  useEffect(() => { checkConfigured(); }, [checkConfigured]);

  return (
    <div className="commerce-wrap">
      <div className="commerce-tabs">
        <button
          className={`commerce-tab-btn ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          🛍️ Products
        </button>
        <button
          className={`commerce-tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ PocketFi Settings {configured === false && <span className="commerce-dot-warn" />}
        </button>
      </div>

      {activeTab === 'products' && configured === false && (
        <div className="commerce-banner">
          Connect your PocketFi account in <button className="commerce-link-btn" onClick={() => setActiveTab('settings')}>Settings</button> before selling products in chat.
        </div>
      )}

      {activeTab === 'products'
        ? <ProductsPanel bId={bId} />
        : <SettingsPanel bId={bId} onSaved={checkConfigured} />}
    </div>
  );
}

// ── Products panel ───────────────────────────────────────────
function ProductsPanel({ bId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null); // null | 'create' | product obj

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProducts(bId);
      setProducts(res.data?.products ?? []);
    } catch { toast.error('Failed to load products'); }
    setLoading(false);
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(p) {
    if (!window.confirm(`Delete "${p.prName}"?`)) return;
    try {
      await deleteProduct(bId, p.prId);
      toast.success('Product deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  }

  return (
    <div className="commerce-panel">
      <div className="commerce-panel-header">
        <span>Products ({products.length})</span>
        <button className="btn btn-sm btn-primary" onClick={() => setModal('create')}>+ Add Product</button>
      </div>

      {loading ? (
        <div className="inbox-loading">Loading…</div>
      ) : products.length === 0 ? (
        <div className="inbox-empty">No products yet. Add one to start selling in chat.</div>
      ) : (
        <ul className="product-list">
          {products.map(p => (
            <li key={p.prId} className="product-item">
              <div className="product-thumb">
                {p.prImageUrl ? <img src={p.prImageUrl} alt={p.prName} /> : <span>🛍️</span>}
              </div>
              <div className="product-meta">
                <div className="product-name">
                  {p.prName}
                  {!Number(p.prIsActive) && <span className="product-badge-inactive">Hidden</span>}
                </div>
                <div className="product-sub">
                  {fmtNaira(p.prPrice)}
                  {p.prStock !== null && p.prStock !== '' && <span className="text-muted"> · {p.prStock} in stock</span>}
                </div>
                <div className="product-id text-muted">ID: {p.prId}</div>
              </div>
              <div className="agent-actions">
                <button className="icon-btn" onClick={() => setModal(p)}>✏️</button>
                <button className="icon-btn danger" onClick={() => handleDelete(p)}>🗑️</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {modal && <ProductModal bId={bId} product={modal === 'create' ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function ProductModal({ bId, product, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: product?.prName ?? '',
    description: product?.prDescription ?? '',
    price: product?.prPrice ?? '',
    imageUrl: product?.prImageUrl ?? '',
    stock: product?.prStock ?? '',
    isActive: product ? !!Number(product.prIsActive) : true,
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!form.name || !form.price || Number(form.price) <= 0) {
      toast.error('Name and a price greater than zero are required'); return;
    }
    setSaving(true);
    try {
      if (product?.prId) {
        await updateProduct(bId, { prId: product.prId, ...form });
        toast.success('Product updated');
      } else {
        await createProduct(bId, form);
        toast.success('Product created');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save product');
    }
    setSaving(false);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{product?.prId ? 'Edit Product' : 'Add Product'}</h3>
          <button className="icon-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <label className="field-label">Name</label>
          <input className="field-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Blue Sneakers" />

          <label className="field-label">Price (₦)</label>
          <input className="field-input" type="number" min="0" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="15000" />

          <label className="field-label">Description <span className="text-muted">(optional)</span></label>
          <input className="field-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Size 42, blue canvas" />

          <label className="field-label">Image URL <span className="text-muted">(optional)</span></label>
          <input className="field-input" value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} placeholder="https://…" />

          <label className="field-label">Stock <span className="text-muted">(optional — leave blank for unlimited)</span></label>
          <input className="field-input" type="number" min="0" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} placeholder="e.g. 12" />

          {product?.prId && (
            <label className="commerce-checkbox">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
              Visible / sellable
            </label>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Product'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Settings panel — PocketFi merchant connection ────────────
function SettingsPanel({ bId, onSaved }) {
  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState({ businessId: '', apiKey: '', secretKey: '' });
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPocketfiMerchant(bId);
      setConfig(res.data?.configured ? res.data.config : null);
    } catch { toast.error('Failed to load PocketFi settings'); }
    setLoading(false);
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    if (!form.businessId || !form.apiKey || !form.secretKey) {
      toast.error('Business ID, API key, and secret key are all required'); return;
    }
    setSaving(true);
    try {
      await savePocketfiMerchant(bId, form);
      toast.success('PocketFi connected');
      setForm({ businessId: '', apiKey: '', secretKey: '' });
      load();
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    }
    setSaving(false);
  }

  async function handleDisable() {
    if (!window.confirm('Disable in-chat commerce for this bot? Existing orders are kept, but no new payment links will be created.')) return;
    try {
      await disablePocketfiMerchant(bId);
      toast.success('In-chat commerce disabled');
      load();
      onSaved?.();
    } catch { toast.error('Failed to disable'); }
  }

  function copyWebhookUrl() {
    if (!config?.webhookUrl) return;
    navigator.clipboard.writeText(config.webhookUrl);
    toast.success('Webhook URL copied');
  }

  if (loading) return <div className="inbox-loading">Loading…</div>;

  return (
    <div className="commerce-panel">
      <div className="commerce-panel-header"><span>PocketFi Merchant Account</span></div>
      <div className="commerce-settings-body">
        <p className="text-muted commerce-intro">
          Connect your own PocketFi account so customers pay you directly — CmdKit never touches this money.
        </p>

        {config && (
          <div className="commerce-connected-box">
            <div className="field-label">Business ID</div>
            <div className="commerce-readonly">{config.businessId}</div>
            <div className="field-label">API Key</div>
            <div className="commerce-readonly">{config.apiKeyMasked}</div>

            <div className="field-label">Webhook URL <span className="text-muted">(paste this into PocketFi → Settings → Webhooks)</span></div>
            <div className="commerce-webhook-row">
              <div className="commerce-readonly commerce-webhook-url">{config.webhookUrl}</div>
              <button className="btn btn-outline btn-sm" onClick={copyWebhookUrl}>Copy</button>
            </div>

            <button className="btn-danger-outline commerce-disable-btn" onClick={handleDisable}>Disable Commerce</button>
          </div>
        )}

        <div className="commerce-form-title">{config ? 'Reconnect / Update Credentials' : 'Connect PocketFi'}</div>

        <label className="field-label">Business ID</label>
        <input className="field-input" value={form.businessId} onChange={e => setForm(f => ({ ...f, businessId: e.target.value }))} placeholder="e.g. 29492" />

        <label className="field-label">API Key</label>
        <input className="field-input" value={form.apiKey} onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))} placeholder="sk_live_…" />

        <label className="field-label">Secret Key</label>
        <input className="field-input" type="password" value={form.secretKey} onChange={e => setForm(f => ({ ...f, secretKey: e.target.value }))} placeholder="••••••••" />

        <button className="btn btn-primary commerce-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : config ? 'Update Credentials' : 'Connect PocketFi'}
        </button>
      </div>
    </div>
  );
}